/**
 * src/lib/voice/isAcceptableAudioFile.js
 *
 * Three signals collapse into a yes/no answer:
 *   1. mime starts with 'audio/'                        (most reliable; trust OS)
 *   2. filename has a known audio extension              (trust the user)
 *   3. NAME-PATTERN heuristic for extensionless audio files
 *      from third-party iOS recorders (e.g. 'AUDIO-2026-03-09-19-44-47'
 *      with type='' and no extension — picker greys these out, but
 *      iOS flags them as audio). Heuristic: match common voice-recorder
 *      naming patterns AND require minimum size (skip empty/header-only files).
 *
 * Bug history:
 *   - 2026-05-03 morning: strict mime check rejected iOS Voice Memos
 *     (file.type='' or 'audio/x-m4a'). Fix #1 added extension fallback.
 *   - 2026-05-03 09:11: CEO reported third-party recorder files named
 *     'AUDIO-2026-03-09-19-44-47' (no extension, file.type='') still
 *     greyed out in iOS picker AND rejected by extension fallback.
 *     Fix #2 added the name-pattern heuristic + dropped the strict picker
 *     accept attribute to '*\/*' so iOS shows the files at all.
 *
 * The combined gate: picker permissive (*\/*), this validator best-effort,
 * Whisper backend authoritative — if any layer says no, user gets a clear
 * toast.
 */

const AUDIO_EXTENSION_RE = /\.(m4a|m4b|mp3|mp4|wav|webm|ogg|oga|opus|caf|aac|flac|mpga|mpeg)$/i;

// Common third-party voice-recorder filename patterns (case-insensitive).
// Tightening this surface is safer than blanket-accepting every extensionless
// file. New patterns can be added as we encounter recorder apps in the wild.
const VOICE_RECORDER_NAME_RE = /^(AUDIO[-_]|REC[-_]|RECORDING[-_]|VOICE[-_]|VN[-_]|MEMO[-_]|NOTE[-_])/i;

const MIN_PROBABLY_AUDIO_BYTES = 10 * 1024; // 10 KB — skip headers/empties

export function isAcceptableAudioFile(file) {
  if (!file || typeof file !== 'object') return false;
  const mime = typeof file.type === 'string' ? file.type : '';
  const name = typeof file.name === 'string' ? file.name : '';
  const size = typeof file.size === 'number' ? file.size : 0;

  // Signal 1: trustworthy mime
  if (mime.startsWith('audio/')) return true;

  // Signal 2: trustworthy extension
  if (AUDIO_EXTENSION_RE.test(name)) return true;

  // Signal 3: extensionless heuristic for iOS third-party recorders.
  // Requires (a) recognized recorder filename pattern AND (b) minimum size.
  // Picks up CEO's bug case 'AUDIO-2026-03-09-19-44-47' but rejects
  // arbitrary extensionless garbage.
  if (
    !name.includes('.') &&
    VOICE_RECORDER_NAME_RE.test(name) &&
    size >= MIN_PROBABLY_AUDIO_BYTES
  ) {
    return true;
  }

  return false;
}
